import { Fragment, memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import classNames from 'classnames';
import { CRITICAL, WARNING } from '@/features/batteryOverview/utils/BatteryDataUtils';

const wrapperClassNames = classNames('display-flex flex-column align-items-start margin-5');

export const SohTooltipBody = memo(({ status }: { status: string }) => {
    const content = [
        <Fragment key={1}>
            <span>
                <FormattedMessage id={`intl-msg:batteryService.batteryStateOfHealth.tooltip.${status.toLowerCase()}`} />
            </span>
        </Fragment>,
    ];

    if ([WARNING, CRITICAL].includes(status)) {
        content.push(
            <Fragment key={2}>
                <ul>
                    <li>
                        <FormattedMessage id="intl-msg:batteryService.batteryStateOfHealth.tooltip.body" />
                    </li>
                    <li>
                        <FormattedMessage id="intl-msg:batteryService.batteryStateOfHealth.tooltip.body2" />
                    </li>
                </ul>
                <hr className={'margin-0 margin-bottom-5'} />
                <span className={'text-bold'}>
                    <FormattedMessage id="intl-msg:batteryService.batteryStateOfHealth.tooltip.recommendation" />
                </span>
                <span>
                    <FormattedMessage id="intl-msg:batteryService.batteryStateOfHealth.tooltip.recommendation2" />
                </span>
                <span>
                    <FormattedMessage id="intl-msg:batteryService.batteryStateOfHealth.tooltip.recommendation3" />
                </span>
            </Fragment>,
        );
    }
    return (
        <div className={wrapperClassNames} data-testid="tooltip-overlay">
            {content}
        </div>
    );
});

export const GenericTooltipBody = memo(({ messageId }: { messageId: string }) => (
    <div className={wrapperClassNames} data-testid="tooltip-overlay">
        <span>
            <FormattedMessage id={messageId} />
        </span>
    </div>
));

export const useBatteryOverviewTooltips = (isLoading: boolean, isError: boolean, sohStatus?: string) => {
    const createMemoizedTooltip = (messageId: string, isLoading: boolean, isError: boolean) => {
        return useMemo(() => {
            if (isLoading || isError) return <></>;
            return (
                <Tooltip textAlignment="left" width={350}>
                    <GenericTooltipBody messageId={messageId} />
                </Tooltip>
            );
        }, [isLoading, isError]);
    };

    const createMemoizedSohTooltip = (isLoading: boolean, isError: boolean, sohStatus?: string) => {
        return useMemo(() => {
            if (isLoading || isError || !sohStatus) return <></>;
            return (
                <Tooltip textAlignment="left" width={350}>
                    <SohTooltipBody status={sohStatus} />
                </Tooltip>
            );
        }, [isLoading, isError]);
    };

    const sohTooltip = createMemoizedSohTooltip(isLoading, isError, sohStatus);

    const fullChargeEnergyTooltip = createMemoizedTooltip(
        'intl-msg:batteryService.fullChargeEnergy.tooltip',
        isLoading,
        isError,
    );

    const fullChargeRangeTooltip = createMemoizedTooltip(
        'intl-msg:batteryService.fullChargeRange.tooltip',
        isLoading,
        isError,
    );

    const energyThroughputTooltip = createMemoizedTooltip(
        'intl-msg:batteryService.dischargedEnergyThroughput.tooltip',
        isLoading,
        isError,
    );

    const chargedEnergyFastChargeTooltip = createMemoizedTooltip(
        'intl-msg:batteryService.chargedEnergyFastCharge.tooltip',
        isLoading,
        isError,
    );

    return {
        sohTooltip,
        fullChargeEnergyTooltip,
        fullChargeRangeTooltip,
        energyThroughputTooltip,
        chargedEnergyFastChargeTooltip,
    };
};
